/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { FC, Fragment, useState, useMemo, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useField } from 'formik';
import { Headline, Body } from '@airwallex/typography';
import { CurrencySelector } from '@airwallex/forms';
import NumberFormat from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { useMobile } from '@/hooks/useMediaQuery';
import { breakpoints, formatCurrency, space } from '@airwallex/global';
import { amountContainerCss, fixAmountCss, amountCss, mobileTextAlignCenter } from './styles';
import { LinkInfoData, Option } from './types';
import Input from '@/components/form/input';
import CurrencyField from '@/components/common/currencyField';

type Props = {
  linkInfo: LinkInfoData;
  currencies: Option[];
  fixedCurrency?: string;
  defaultAmount?: number | string;
  defaultCurrency?: string;
};

const AMOUNT_FIELD_NAME = 'amount';
const CURRENCY_FIELD_NAME = 'currency';

const Index: FC<Props> = ({ linkInfo, currencies, fixedCurrency, defaultAmount, defaultCurrency }) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { path } = useRouteMatch();
  const isPreview = path === '/pay/link/preview';
  const [currencyQuery, setCurrencyQuery] = useState('');
  const [amountField, , amountHelper] = useField(AMOUNT_FIELD_NAME);
  const [currencyField, , currencyHelper] = useField(CURRENCY_FIELD_NAME);
  const currencyValue = useMemo(() => currencyField.value || defaultCurrency, [currencyField.value, defaultCurrency]);
  const zeroFormat = useMemo(
    () =>
      formatCurrency({
        amount: 0,
        currency: currencyValue,
        showCode: false,
      }),
    [currencyValue]
  );
  const decimalScale = useMemo(() => {
    const hasDot = zeroFormat.indexOf('.') !== -1;
    return hasDot ? zeroFormat.split('.')[1].length : 0;
  }, [zeroFormat]);
  useEffect(() => {
    const formattedAmount = formatCurrency({
      amount: amountField.value,
      currency: currencyValue,
      showCode: false,
    });
    amountHelper.setValue(formattedAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeroFormat]);

  return (
    <Fragment>
      {!linkInfo?.amount && (
        <div
          css={css`
            margin-top: ${space.xl};
            ${breakpoints.breakpoint.s} {
              margin-top: ${space.s};
              margin-bottom: 0;
            }
          `}
        >
          {!isMobile && <Body variant="100">{t('pay_by_link.amount')}</Body>}
          <div css={amountContainerCss}>
            <NumberFormat
              autoFocus={!isPreview}
              fixedDecimalScale={true}
              decimalScale={decimalScale}
              thousandSeparator={false}
              allowNegative={false}
              type="text"
              placeholder={zeroFormat}
              css={amountCss(fixedCurrency)}
              suffixComponent={fixedCurrency}
              customInput={Input}
              name={AMOUNT_FIELD_NAME}
              defaultValue={defaultAmount}
              onChange={() => {
                amountHelper.setTouched(true);
              }}
            />
            {!fixedCurrency && (
              <CurrencySelector
                css={css`
                  &:nth-child(2) {
                    .airwallex-select__control {
                      border-top-left-radius: 0;
                      border-bottom-left-radius: 0;
                      &:not(:hover):not(.airwallex-select__control--is-focused) {
                        border-left-color: transparent;
                      }
                    }
                  }
                  display: flex;
                  align-items: center;
                  margin-bottom: 0;
                  height: fit-content;
                  padding: ${space.xs} 0;
                  .airwallex-select__control {
                    width: 139px;
                    height: 56px;
                  }
                  .airwallex-select__menu {
                    width: max-content;
                    max-width: calc(100vw - 32px);
                    right: 0;
                  }
                  .airwallex-select__value-container {
                    & > div {
                      ${currencyQuery ? 'visibility: hidden' : ''};
                      & > div {
                        font-size: 18px;
                        & > div {
                          display: flex;
                          align-items: center;
                        }
                      }
                    }
                    span {
                      position: absolute;
                      font-size: 18px;
                      font-weight: 700;
                    }
                  }
                `}
                searchable
                filterOption={(comparison, query) => {
                  if (currencyQuery !== query) {
                    setCurrencyQuery(query);
                  }
                  const option = comparison.data as Option;
                  return [option.value, option.subLabel, option.subLabelEn].some((value) =>
                    value.toLowerCase().includes(query.toLowerCase())
                  );
                }}
                value={currencyValue}
                label=""
                name={CURRENCY_FIELD_NAME}
                options={currencies}
                onChange={(ccy: string) => {
                  setCurrencyQuery('');
                  currencyHelper.setValue(ccy);
                }}
              />
            )}
          </div>
        </div>
      )}
      {fixedCurrency && (
        <Body variant="100.Subtle" css={mobileTextAlignCenter}>
          {t('pay_by_link.description.greater_amount')} {linkInfo?.minAmount?.amount} {linkInfo?.minAmount?.ccy}
        </Body>
      )}
      {/* fix amount */}
      {linkInfo?.amount &&
        (isMobile ? (
          <Headline variant="100" css={fixAmountCss}>
            {formatCurrency({
              amount: linkInfo.amount.amount,
              currency: linkInfo.amount.ccy,
            })}
          </Headline>
        ) : (
          <CurrencyField
            css={css`
              margin-top: ${space.xl};
            `}
            label={t('checkout.order_amount')}
            currency={linkInfo.amount.ccy}
            amount={linkInfo.amount.amount}
            type="primary"
          />
        ))}
    </Fragment>
  );
};

export default Index;
